import RecordableTrack from './RecordableTrack.js';
import {TRIGGER} from 'messaging/message-types.js';
import Tone from 'tone';
import SoundUploader from 'api/SoundUploader.js';
import {AUDIO} from 'constants/track-types.js';
import {
    OFF,
    PREPARING,
    RECORDING,
    STOPPING} from 'constants/recording-modes.js';

class AudioTrack extends RecordableTrack {
    constructor(trackNumber, sequencerEngine, mixSession, numberOfPatterns, update, subSequence) {
        super(trackNumber, sequencerEngine, mixSession, numberOfPatterns, update, subSequence);
        this.setupEffects();

        this.recordingMode = OFF;

        this.deleteInlet("trigger ");
        this.deleteInlet("audition sample");
        this.deleteInlet("restart");

        this.deleteOutlet(TRIGGER);
    }

    get selectedRecording() {
        return this.trackData.selectedRecording;
    }

    set selectedRecording(x) {
        this.trackData.selectedRecording = x;
    }

    isRecorable() {
        return true;
    }

    getCopy() {
        let track = super.getCopy((a,b,c,d,e,f) => new AudioTrack(a,b,c,e,d,e,f));
        track.selectedRecording = this.selectedRecording;
        return track;
    }

    getName() {
        return "Audio Track";
    }

    getSelectedRecording() {
        return this.selectedRecording;
    }

    setSelectedRecording(recording) {
        this.selectedRecording = recording;
    }

    getRecordings() {
        return super.getSamplers();
    }

    saveBuffer(blob) {
        super.saveBuffer(blob);
        this.setSelectedRecording(0);
    }

    getJson() {
        let json = super.getJson();
        json.selectedRecording = this.selectedRecording;
        return json;
    }

    /*
    fromJson(json) {
        this.selectedRecording = json.selectedRecording;
    }
    */

    getSelectedRecordingSampler() {
        return super.getSamplers()[this.selectedRecording];
    }

    getSelectedRecordingSoundId() {
        return this.soundIds[this.selectedRecording];
    }

    getTrackType() {
        return AUDIO;
    }

    toggleEnabled() {
        super.toggleEnabled();
    }

    setIsEnabled(isEnabled) {
        super.setIsEnabled(isEnabled);
        let input = this.audioInputManager;
        if (!input) {
            return;
        }
        input.monitorVolume.gain.value = this.isEnabled ? 1 : 0;
    }

    get length() {
        // we use the length of the sample to determine the length
        if (this.frozenColumns.length === 0 ||
            this.frozenColumns.every(col => !col.frozenSamplers[col.playing])) {
            return super.length;
        }
        let samplers = this.frozenColumns.map(
            col => col.frozenSamplers[col.playing])
            .filter(x => x);
        let dur = this.getMaxDuration();
        let tempoRatio = samplers[0].tempo / this.sequencerEngine.bpm;
        let len = roundTo32(tempoRatio * dur / Tone.Time('32n').toSeconds());
        return len;
    }

    getMaxDuration() {
        let samplers = this.frozenColumns.map(
            col => col.frozenSamplers[col.playing])
            .filter(x => x);
        if (samplers.length === 0) {
            return 1;
        }
        return Math.max(... samplers.map(x => x.getDuration())); // in seconds
    }

    get isAudioTrack() {
        return true;
    }

    scheduleAnyRecording(patternStep, time) {
        // time += 0.01;
        if (patternStep % (this.length / 2) !== 0 && patternStep % (this.length) !== 0) {
            this.countdown = Math.min((this.length / 2) - (patternStep % (this.length / 2)),
                                      this.length - (patternStep % this.length));
            return;
        }

        if (this.recordingMode === PREPARING) {
            this.startRecording(time);
            this.recordingMode = RECORDING;
        } else if (this.recordingMode === STOPPING) {
            this.stopRecording(time);
            this.recordingMode = OFF;
        }
    }

    getSampler() {
        let samplers = this.frozenColumns.map(x => x.frozenSamplers[x.playing])
            .filter(x => x);
        return samplers[0];
    }
}

const roundTo32 = (len) => {
    let rounded = Math.round(len);
    let off = len % 32;
    return len - off;
};

export default AudioTrack;
